import { fromJS } from "immutable";
import {
  normalizeDetailedShipment,
  normalizeItems,
} from "../../../../reduxFolder/reducers/storage";
import demoItem1Pic1 from "./images/demo-item1-1.jpeg";
import demoItem1Pic2 from "./images/demo-item1-2.jpeg";
import demoItem1Pic3 from "./images/demo-item1-3.jpeg";
import demoItem2Pic1 from "./images/demo-item2-1.jpeg";
import demoItem2Pic2 from "./images/demo-item2-2.jpeg";
import demoItem3Pic1 from "./images/demo-item3-1.jpeg";
import demoItem3Pic2 from "./images/demo-item3-2.jpeg";
import demoShipment1Box from "./images/demo-shipment1-box.jpeg";
import demoShipment1Label from "./images/demo-shipment1-label.jpeg";
import demoShipment2Box from "./images/demo-shipment2-box.jpeg";
import demoShipment2Label from "./images/demo-shipment2-label.jpeg";
import demoShipment3Box from "./images/demo-shipment3-box.jpeg";
import demoShipment3Label from "./images/demo-shipment3-label.jpeg";

// Negative ids can never collide with real backend ids, so the seeded
// detailedShipment entries and DOM data-parcel-id attributes stay unambiguous.
export const DEMO_SHIPMENT_IDS = [-1, -2, -3];

export const daysAgo = (days: number) =>
  new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();

// The detail cache is seeded directly (not through normalizeImages), so
// pictures must already be in the normalized { medium, large } shape.
export const demoPicture = (url: string) => ({ medium: url, large: url });

// The one demo shipment that showcases a browsable multi-item inventory.
const DEMO_INVENTORY_SHIPMENT_ID = -1;

// Item weights sum to the parcel's 2.87 kg. status is lowercase "active" on
// purpose: InventoryList lowercases it before grouping ("In storage"), while
// InventoryItemRow's isCartableItem matches exact-case "Active" — so demo item
// checkboxes render disabled without touching the row component.
// demo-itemN-*.jpeg are real product photos, two or three per item, mapping to
// the Nth entry; adjust descriptions/weights here when swapping photos.
const rawDemoInventoryItems = [
  {
    id: -101,
    sku: "I-DEMO-1",
    shipment_id: DEMO_INVENTORY_SHIPMENT_ID,
    status: "active",
    description: "Jig Saw",
    weight: 1.21,
    quantity: 1,
    customer_comment: null,
    addons: [],
    created_at: daysAgo(2),
    pictures: [
      demoPicture(demoItem1Pic1),
      demoPicture(demoItem1Pic2),
      demoPicture(demoItem1Pic3),
    ],
    // Stays on the first item: the parcel-level customs tab shows
    // items[0].customs_declarations.
    customs_declarations: [
      {
        id: -1001,
        description: "Jig Saw",
        hs_code: "846722",
        quantity: 1,
        value: 179.99,
      },
    ],
  },
  {
    id: -102,
    sku: "I-DEMO-2",
    shipment_id: DEMO_INVENTORY_SHIPMENT_ID,
    status: "active",
    description: "Sneakers",
    weight: 1.38,
    quantity: 1,
    customer_comment: null,
    addons: [],
    created_at: daysAgo(2),
    pictures: [demoPicture(demoItem2Pic1), demoPicture(demoItem2Pic2)],
    customs_declarations: [
      {
        id: -1002,
        description: "Sneakers",
        hs_code: "640419",
        quantity: 1,
        value: 109.99,
      },
    ],
  },
  {
    id: -103,
    sku: "I-DEMO-3",
    shipment_id: DEMO_INVENTORY_SHIPMENT_ID,
    status: "active",
    description: "Wireless mouse",
    weight: 0.28,
    quantity: 1,
    customer_comment: null,
    addons: [],
    created_at: daysAgo(2),
    pictures: [demoPicture(demoItem3Pic1), demoPicture(demoItem3Pic2)],
    customs_declarations: [
      {
        id: -1003,
        description: "Wireless mouse",
        hs_code: "847160",
        quantity: 1,
        value: 79,
      },
    ],
  },
];

// store_name is demo-only: real list payloads carry a backend-computed
// emblem_thumb_url instead. DemoShipments resolves store_name against the
// expected-shipments shop catalog at render time, so row logos use the same
// emblem assets as real parcels.
const rawDemoShipments = [
  {
    id: -1,
    description: "532916641319",
    store_name: "Calvin Klein",
    weight: 2.87,
    received_at: daysAgo(2),
    state: "received",
    service_requests_count: [],
    items_quantity: rawDemoInventoryItems.length,
    items: [],
    emblem_thumb_url: null,
    package_type: "box",
    prohibited: false,
    customer_comment: null,
    consolidation_id: null,
    addons: [],
  },
  {
    id: -2,
    description: "532092761354",
    store_name: "Tommy Hilfiger",
    weight: 0.45,
    received_at: daysAgo(5),
    state: "received",
    service_requests_count: [],
    items_quantity: 1,
    items: [],
    emblem_thumb_url: null,
    package_type: "envelope",
    prohibited: false,
    customer_comment: "iPhone case + charger",
    consolidation_id: null,
    addons: [],
  },
  {
    id: -3,
    description: "TBA332648215793",
    store_name: "Amazon",
    weight: 1.24,
    received_at: daysAgo(9),
    state: "received",
    service_requests_count: [],
    items_quantity: 1,
    items: [],
    emblem_thumb_url: null,
    package_type: "box",
    prohibited: false,
    customer_comment: null,
    consolidation_id: null,
    addons: [],
  },
];

const rawDemoDetails = [
  {
    ...rawDemoShipments[0],
    pictures: [demoPicture(demoShipment1Box), demoPicture(demoShipment1Label)],
    service_requests: [],
    items: rawDemoInventoryItems,
  },
  {
    ...rawDemoShipments[1],
    pictures: [demoPicture(demoShipment2Box), demoPicture(demoShipment2Label)],
    service_requests: [],
    items: [
      {
        id: -201,
        weight: 0.45,
        description: "532092761354",
        pictures: [],
        customs_declarations: [
          {
            id: -2001,
            description: "Phone case",
            hs_code: "392690",
            quantity: 2,
            value: 12.5,
          },
          {
            id: -2002,
            description: "USB charging cable",
            hs_code: "854442",
            quantity: 1,
            value: 9.99,
          },
        ],
      },
    ],
  },
  {
    ...rawDemoShipments[2],
    pictures: [demoPicture(demoShipment3Box), demoPicture(demoShipment3Label)],
    service_requests: [],
    items: [
      {
        id: -301,
        weight: 1.24,
        description: "TBA332648215793",
        pictures: [],
        customs_declarations: [
          {
            id: -3001,
            description: "Ceramic mug",
            hs_code: "691200",
            quantity: 2,
            value: 18,
          },
          {
            id: -3002,
            description: "LED desk lamp",
            hs_code: "940520",
            quantity: 1,
            value: 34.9,
          },
        ],
      },
    ],
  },
];

// Same normalizers the reducer applies to real API responses, so the fixtures
// match the post-reducer store shape exactly.
export const demoStorageItems = normalizeItems(fromJS(rawDemoShipments));

export const demoDetailedShipments = fromJS(rawDemoDetails).map(
  normalizeDetailedShipment,
);

// The demo inventory shipment's items, for the "Items" search view — already
// normalized (same store shape as an /items search response).
export const demoInventoryItems = demoDetailedShipments
  .find((shipment: any) => shipment.get("id") === DEMO_INVENTORY_SHIPMENT_ID)
  .get("items");

// Expected-shipments demo rows: ids -90x, their customs declarations -90xx.
// Verbatim /expected_shipments list-payload shape (Expected applies no
// normalizer). customs_declarations are pre-attached because the demo panel
// skips the lazy per-item GET that normally grafts them on.
const rawDemoExpectedItems = [
  {
    id: -901,
    tracking_number: "1Z8843W20312456784",
    order_number: "112-6402271-8014653",
    store_name: "Amazon",
    // Empty string, not null: the edit form's yup schema rejects null comments.
    customer_comment: "",
    created_at: daysAgo(1),
    service_requests: [],
    expected_item_addons: [],
    customs_declarations: [
      {
        id: -9011,
        description: "Running shoes",
        hs_code: "640419",
        quantity: 1,
        value: 119.99,
      },
    ],
  },
  {
    id: -902,
    tracking_number: "RB644790125CN",
    order_number: "24-11857-30122",
    store_name: "Ebay",
    customer_comment: "Birthday gift",
    created_at: daysAgo(4),
    service_requests: [],
    expected_item_addons: [],
    customs_declarations: [
      {
        id: -9021,
        description: "Wireless headphones",
        hs_code: "851830",
        quantity: 1,
        value: 89.99,
      },
    ],
  },
  {
    id: -903,
    tracking_number: "LZ982004115US",
    order_number: "ORD-58317",
    store_name: "Adidas",
    customer_comment: "",
    created_at: daysAgo(7),
    service_requests: [],
    expected_item_addons: [],
    customs_declarations: [
      {
        id: -9031,
        description: "Track jacket",
        hs_code: "611130",
        quantity: 1,
        value: 64.5,
      },
    ],
  },
];

export const demoExpectedItems = fromJS(rawDemoExpectedItems);
